import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api, API } from "../lib/api";
import { Countdown } from "../lib/bookmark";

const when = (iso) => { const d = new Date(`${iso}T00:00:00Z`); return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", ...(d.getUTCFullYear() !== new Date().getUTCFullYear() ? { year: "numeric" } : {}), timeZone: "UTC" }); };

function Upcoming({ u }) {
  return (
    <div className="bg-black text-center py-8 space-y-3" data-testid="scroll-upcoming">
      <p className="text-sm text-slate-300">Episode {u.episode} unlocks {when(u.unlocks)}</p>
      <Countdown unlocks={u.unlocks} />
    </div>
  );
}

export function ScrollReader({ slug, onEnd, start, onPos }) {
  const [d, setD] = useState(null);
  const [ep, setEp] = useState(start?.ep || null);
  const root = useRef(null);
  const jumped = useRef(false);
  useEffect(() => { api.get(`/public/books/${slug}/comic/scroll`).then((r) => setD(r.data)).catch(() => setD({ items: [] })); }, [slug]);
  const webtoon = d?.format === "webtoon";
  const eps = useMemo(() => [...new Set((d?.items || []).map((x) => x.episode))].sort((a, b) => a - b), [d]);
  const curEp = webtoon ? (eps.includes(ep) ? ep : eps[0]) : null;
  const items = useMemo(() => (d?.items || []).map((x, k) => ({ ...x, k })).filter((x) => !webtoon || x.episode === curEp), [d, webtoon, curEp]);
  useEffect(() => {
    if (!root.current || !items.length) return undefined;
    const io = new IntersectionObserver((es) => { const v = es.filter((e) => e.isIntersecting).map((e) => Number(e.target.dataset.k)); if (v.length) onPos?.({ mode: "scroll", ep: curEp, item: Math.min(...v) }); }, { threshold: 0.5 });
    root.current.querySelectorAll("[data-k]").forEach((el) => io.observe(el));
    if (!jumped.current && start?.item != null) {
      jumped.current = true;
      setTimeout(() => root.current?.querySelector(`[data-k="${start.item}"]`)?.scrollIntoView({ block: "start" }), 300);
    }
    return () => io.disconnect();
  }, [items, curEp, onPos, start]);
  if (!d) return <div className="h-96" />;
  if (!d.items.length) return d.upcoming?.length ? <Upcoming u={d.upcoming[0]} /> : <p className="text-center text-slate-400 py-20" data-testid="scroll-empty">The author hasn't shared a preview yet.</p>;
  const nextLive = eps.find((e) => e > curEp);
  const nextLocked = d.upcoming?.find((u) => u.episode > (curEp || 0));
  const goEp = (e) => { setEp(e); onPos?.({ mode: "scroll", ep: e, item: d.items.findIndex((x) => x.episode === e) }); window.scrollTo({ top: root.current.offsetTop - 80, behavior: "smooth" }); };
  return (
    <div ref={root} className="mx-auto w-full max-w-[720px] bg-white" data-testid="scroll-reader">
      {webtoon && <div className="bg-black flex items-center justify-between px-3 py-2"><p className="text-amber-200 text-xs" data-testid={`scroll-episode-${curEp}`}>Episode {curEp}</p>
        {eps.length > 1 && <select value={curEp} onChange={(e) => goEp(Number(e.target.value))} className="bg-slate-900 text-xs text-slate-200 rounded px-2 h-7" data-testid="scroll-episode-select">{eps.map((e) => <option key={e} value={e}>Episode {e}</option>)}</select>}</div>}
      {items.map((it) => (
        <div key={it.k} data-k={it.k} className={it.gap ? "bg-white px-2 sm:px-4 pt-3" : ""}>
          <img src={`${API}/public/books/${slug}/comic/pages/${it.page}/panels/${it.panel}`} alt={`Page ${it.page}${it.panel >= 0 ? `, panel ${it.panel + 1}` : ""}`} loading="lazy"
            width={it.w} height={it.h} className="w-full h-auto block" data-testid={`scroll-item-${it.k}`} />
        </div>
      ))}
      {webtoon && nextLive && <div className="bg-black py-8 text-center"><button onClick={() => goEp(nextLive)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-600 hover:bg-amber-500 text-white text-sm transition-colors" data-testid="next-episode">Next episode<ArrowRight size={15} /></button></div>}
      {webtoon && !nextLive && nextLocked && <Upcoming u={nextLocked} />}
      {(!webtoon || !nextLive) && onEnd(d)}
    </div>
  );
}
